// src/components/AdminReviewsDashboard.jsx
import React, { useEffect, useState } from "react";
import config from "../config";
import "../css/theme.css";
import "../css/components.css";
import "./AdminReviewsDashboard.css";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateDMY, toLocalDateStr } from "../utils/date";
import { getUserRole } from "../utils/auth";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";
import { useConfirm } from "./common/useConfirm";
import {
  getReviews,
  approveReply,
  discardDraft,
  regenerateDraft,
  getAverageRatings,
  getReviewOverview,
  getGuestReviews,
  approveGuestReview,
  rejectGuestReview,
} from "../services/ReviewService";

const OVERVIEW_PERIODS = [
  { value: "WEEK", label: "This Week" },
  { value: "MONTH", label: "This Month" },
  { value: "YEAR", label: "This Year" },
];

const PREVIOUS_PERIOD_LABEL = { WEEK: "last week", MONTH: "last month", YEAR: "last year" };

// Reviews-received trend vs. the same point in the previous period (see
// ReviewOverviewDTO.PreviousPeriod - already an apples-to-apples partial-
// period comparison server-side, not "vs the whole prior period"). Teal for
// up / purple for down, matching the app's existing high/low-rating palette
// (Stars component) rather than introducing green/red.
const ReviewCountTrend = ({ current, previous, period }) => {
  if (previous == null) return null;
  const label = PREVIOUS_PERIOD_LABEL[period] || "last period";
  if (previous === 0) {
    if (current === 0) return <div className="review-trend review-trend--neutral">No change vs {label}</div>;
    return <div className="review-trend review-trend--up">New activity vs {label} (had none)</div>;
  }
  const pct = ((current - previous) / previous) * 100;
  const cls = pct > 0 ? "review-trend--up" : pct < 0 ? "review-trend--down" : "review-trend--neutral";
  const sign = pct > 0 ? "+" : "";
  return (
    <div className={`review-trend ${cls}`}>
      {sign}{pct.toFixed(1)}% vs {label}
    </div>
  );
};

// Average-rating trend vs. the same comparison window - only rendered when
// both periods actually have at least one review (averageRating is null
// otherwise), so this never claims a rating moved when there was nothing to
// average on one side.
const AverageRatingTrend = ({ current, previous, period }) => {
  if (current == null || previous == null) return null;
  const label = PREVIOUS_PERIOD_LABEL[period] || "last period";
  const delta = current - previous;
  const cls = delta > 0.001 ? "review-trend--up" : delta < -0.001 ? "review-trend--down" : "review-trend--neutral";
  const sign = delta > 0 ? "+" : "";
  return (
    <div className={`review-trend ${cls}`}>
      {sign}{delta.toFixed(2)}★ vs {label}
    </div>
  );
};

const PAGE_SIZE = 12;

const STATUS_OPTIONS = [
  { value: "NO_REPLY", label: "No reply yet" },
  { value: "AUTO_SENT", label: "Auto-sent" },
  { value: "PENDING_APPROVAL", label: "Pending approval" },
  { value: "SENT", label: "Sent" },
  { value: "DISCARDED", label: "Discarded" },
  { value: "FAILED", label: "Failed" },
];

// Guest-submitted, on-site review moderation status (GuestReviewStatus on
// the backend) - a completely separate status space from GBP's
// ReviewReplyStatus/STATUS_OPTIONS above.
const GUEST_REVIEW_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const GuestReviewStatusBadge = ({ status }) => {
  // neutral/blue/danger only - this app's badge classes already avoid
  // green/red/orange/amber (see review-badge CSS), same hard palette rule
  // every other control here follows.
  const cls = { PENDING: "warn", APPROVED: "blue", REJECTED: "neutral" }[status] || "neutral";
  const label = GUEST_REVIEW_STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
  return <span className={`review-badge review-badge--${cls}`}>{label}</span>;
};

const StatusBadge = ({ status }) => {
  const cls = {
    NO_REPLY: "neutral",
    AUTO_SENT: "success",
    PENDING_APPROVAL: "warn",
    SENT: "blue",
    DISCARDED: "neutral",
    FAILED: "danger",
  }[status] || "neutral";
  const label = STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
  return <span className={`review-badge review-badge--${cls}`}>{label}</span>;
};

const Stars = ({ n }) => (
  <span className={`review-stars ${n <= 3 ? "review-stars--low" : "review-stars--high"}`}>
    {"★".repeat(n)}
    <span className="review-stars-empty">{"★".repeat(5 - n)}</span>
  </span>
);

// Dual-handle 1-5 star range filter. Two overlapping native <input type="range">
// elements sharing one track (the standard no-dependency way to build a range
// slider) rather than pulling in a slider library - this app already prefers
// plain HTML controls (no table/slider/toast lib in use anywhere else either).
const StarRangeSlider = ({ min, max, onChange }) => {
  const pct = (v) => ((v - 1) / 4) * 100;

  const handleMin = (e) => {
    const v = Math.min(Number(e.target.value), max);
    onChange(v, max);
  };
  const handleMax = (e) => {
    const v = Math.max(Number(e.target.value), min);
    onChange(min, v);
  };

  return (
    <div className="star-range">
      <div className="star-range-track">
        <div
          className="star-range-fill"
          style={{ left: `${pct(min)}%`, width: `${pct(max) - pct(min)}%` }}
        />
      </div>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={min}
        onChange={handleMin}
        className="star-range-input"
        aria-label="Minimum star rating"
      />
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={max}
        onChange={handleMax}
        className="star-range-input"
        aria-label="Maximum star rating"
      />
    </div>
  );
};

const AdminReviewsDashboard = () => {
  const { toasts, showToast, dismissToast } = useToast();
  const { confirm, ConfirmDialogElement } = useConfirm();
  const role = getUserRole();
  // This page is reachable by every role via "view_reviews" (see
  // PermissionService.java), but only SUPER_ADMIN/SUPER_USER can actually
  // reply/manage - ADMIN and everyone else gets a read-only view. Matches
  // the same role split enforced server-side on every write endpoint in
  // ReviewManagementController.
  const canManage = role === "SUPER_ADMIN" || role === "SUPER_USER";
  // Stricter than canManage - a resort-by-resort rating breakdown is
  // business-sensitive in a way an individual review list isn't, so this
  // stays SUPER_ADMIN-only, matching the backend endpoint's own gating.
  const isSuperAdmin = role === "SUPER_ADMIN";

  const [activeTab, setActiveTab] = useState("REVIEWS");

  // --- Average rating summary (SUPER_ADMIN only) ---
  const [ratingSummary, setRatingSummary] = useState(null);
  const [ratingSummaryLoading, setRatingSummaryLoading] = useState(false);

  // --- Reviews tab state ---
  const [resorts, setResorts] = useState([]);
  const [selectedResort, setSelectedResort] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [ratingMin, setRatingMin] = useState(1);
  const [ratingMax, setRatingMax] = useState(5);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [busyIds, setBusyIds] = useState(new Set());

  // --- Overview tab state ---
  const [overviewResort, setOverviewResort] = useState(null);
  const [overviewPeriod, setOverviewPeriod] = useState("MONTH");
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // --- Website Reviews tab state (guest-submitted, on-site reviews) ---
  const [guestReviewResort, setGuestReviewResort] = useState(null);
  const [guestReviewStatus, setGuestReviewStatus] = useState(GUEST_REVIEW_STATUS_OPTIONS[0]); // default to Pending
  const [guestReviews, setGuestReviews] = useState([]);
  const [guestReviewsPage, setGuestReviewsPage] = useState(0);
  const [guestReviewsTotalPages, setGuestReviewsTotalPages] = useState(0);
  const [guestReviewsLoading, setGuestReviewsLoading] = useState(false);
  const [guestReviewBusyIds, setGuestReviewBusyIds] = useState(new Set());

  useEffect(() => {
    const fetchResorts = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}/api/resorts/resort-names-drop-down`, { headers: config.getHeaders() });
        const data = await res.json();
        setResorts(data.map((r) => ({ value: r.id, label: r.name })));
      } catch (err) {
        console.error("Failed to fetch resorts", err);
      }
    };
    fetchResorts();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [selectedResort, statusFilter, ratingMin, ratingMax, fromDate, toDate]);

  const fetchReviews = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReviews(
        {
          resortId: selectedResort?.value,
          status: statusFilter?.value,
          ratingMin,
          ratingMax,
          fromDate: fromDate ? toLocalDateStr(fromDate) : undefined,
          toDate: toDate ? toLocalDateStr(toDate) : undefined,
        },
        page,
        PAGE_SIZE
      );
      setReviews(data?.content || []);
      setTotalPages(data?.totalPages ?? 0);
      setTotalElements(data?.totalElements ?? 0);
    } catch (err) {
      console.error(err);
      showToast(err.message, "danger");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResort, statusFilter, ratingMin, ratingMax, fromDate, toDate, page]);

  // Deliberately NOT scoped to the page's own resort/status/date filters -
  // this is meant to read like an inbox badge ("N things need a reply,
  // total"), not "pending within whatever you're currently browsing".
  const fetchPendingCount = React.useCallback(async () => {
    try {
      const data = await getReviews({ status: "PENDING_APPROVAL" }, 0, 1);
      setPendingCount(data?.totalElements ?? 0);
    } catch (err) {
      console.error("Failed to fetch pending reply count", err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "REVIEWS") {
      fetchReviews();
      fetchPendingCount();
    }
  }, [activeTab, fetchReviews, fetchPendingCount]);

  useEffect(() => {
    // Needed on both tabs now: Reviews tab's Average Rating card, and
    // Overview tab's all-time "Vintara Avg Rating" box.
    if ((activeTab !== "REVIEWS" && activeTab !== "OVERVIEW") || !isSuperAdmin) return;
    const fetchRatingSummary = async () => {
      setRatingSummaryLoading(true);
      try {
        setRatingSummary(await getAverageRatings());
      } catch (err) {
        console.error("Failed to fetch rating summary", err);
      } finally {
        setRatingSummaryLoading(false);
      }
    };
    fetchRatingSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isSuperAdmin]);

  const fetchOverview = React.useCallback(async () => {
    setOverviewLoading(true);
    try {
      setOverviewData(await getReviewOverview(overviewResort?.value, overviewPeriod));
    } catch (err) {
      console.error(err);
      showToast(err.message, "danger");
    } finally {
      setOverviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewResort, overviewPeriod]);

  useEffect(() => {
    if (activeTab === "OVERVIEW") fetchOverview();
  }, [activeTab, fetchOverview]);

  const fetchGuestReviews = React.useCallback(async () => {
    setGuestReviewsLoading(true);
    try {
      const data = await getGuestReviews(
        { resortId: guestReviewResort?.value, status: guestReviewStatus?.value },
        guestReviewsPage,
        PAGE_SIZE
      );
      setGuestReviews(data?.content || []);
      setGuestReviewsTotalPages(data?.totalPages ?? 0);
    } catch (err) {
      console.error(err);
      showToast(err.message, "danger");
    } finally {
      setGuestReviewsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestReviewResort, guestReviewStatus, guestReviewsPage]);

  useEffect(() => {
    setGuestReviewsPage(0);
  }, [guestReviewResort, guestReviewStatus]);

  useEffect(() => {
    if (activeTab === "WEBSITE_REVIEWS") fetchGuestReviews();
  }, [activeTab, fetchGuestReviews]);

  const handleApproveGuestReview = async (review) => {
    const ok = await confirm({
      title: "Approve this review?",
      message: "This review will become publicly visible on the resort's microsite.",
      confirmLabel: "Approve",
    });
    if (!ok) return;
    setGuestReviewBusyIds((prev) => new Set(prev).add(review.id));
    try {
      await approveGuestReview(review.id);
      showToast("Review approved", "success");
      fetchGuestReviews();
    } catch (err) {
      console.error(err);
      showToast(err.message, "danger");
    } finally {
      setGuestReviewBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(review.id);
        return next;
      });
    }
  };

  const handleRejectGuestReview = async (review) => {
    const ok = await confirm({
      title: "Reject this review?",
      message: "This review will never be shown on the resort's microsite.",
      confirmLabel: "Reject",
      danger: true,
    });
    if (!ok) return;
    setGuestReviewBusyIds((prev) => new Set(prev).add(review.id));
    try {
      await rejectGuestReview(review.id);
      showToast("Review rejected", "success");
      fetchGuestReviews();
    } catch (err) {
      console.error(err);
      showToast(err.message, "danger");
    } finally {
      setGuestReviewBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(review.id);
        return next;
      });
    }
  };

  const resetFilters = () => {
    setSelectedResort(null);
    setStatusFilter(null);
    setRatingMin(1);
    setRatingMax(5);
    setFromDate(null);
    setToDate(null);
  };

  const withBusy = async (id, fn) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await fn();
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const openDraft = (review) => {
    setExpandedId(review.id);
    // aiDraftText for a Gemini draft, replyText for anything already
    // rendered/sent (an already-sent reply being corrected, or - for
    // older reviews - a reply rendered from the now-removed template
    // feature) - blank otherwise, for a fully manual reply. Manual typing
    // is the one always-available fallback if Gemini isn't working.
    setDraftText(review.aiDraftText || review.replyText || "");
  };

  const closeDraft = () => {
    setExpandedId(null);
    setDraftText("");
  };

  const handleSend = async (review) => {
    if (!draftText.trim()) {
      return showToast("Reply can't be empty", "warning");
    }
    const ok = await confirm({
      title: "Send this reply?",
      message: "This will post publicly to Google as your business's reply. This can't be undone.",
      confirmLabel: "Send",
    });
    if (!ok) return;
    await withBusy(review.id, async () => {
      try {
        const updated = await approveReply(review.id, draftText.trim());
        setReviews((prev) => prev.map((r) => (r.id === review.id ? updated : r)));
        showToast("Reply sent", "success");
        closeDraft();
        fetchPendingCount();
      } catch (err) {
        console.error(err);
        showToast(err.message, "danger");
      }
    });
  };

  const handleDiscard = async (review) => {
    const ok = await confirm({
      title: "Discard this reply?",
      message: "This reply will be discarded without sending.",
      confirmLabel: "Discard",
      danger: true,
    });
    if (!ok) return;
    await withBusy(review.id, async () => {
      try {
        const updated = await discardDraft(review.id, "");
        setReviews((prev) => prev.map((r) => (r.id === review.id ? updated : r)));
        showToast("Draft discarded", "success");
        closeDraft();
        fetchPendingCount();
      } catch (err) {
        console.error(err);
        showToast(err.message, "danger");
      }
    });
  };

  const handleRegenerate = async (review) => {
    await withBusy(review.id, async () => {
      try {
        const updated = await regenerateDraft(review.id);
        setReviews((prev) => prev.map((r) => (r.id === review.id ? updated : r)));
        setDraftText(updated.aiDraftText || "");
        showToast("New draft generated", "success");
      } catch (err) {
        console.error(err);
        showToast(err.message, "danger");
      }
    });
  };


  // Average Rating card on the Reviews tab tracks the resort filter - a
  // specific resort's own average when one is picked, the overall average
  // otherwise. Same data source (ratingSummary) either way, just picking
  // which figure out of it, so no extra API call.
  const selectedResortRatingStat = selectedResort
    ? ratingSummary?.perResort?.find((r) => r.resortId === selectedResort.value)
    : null;
  const avgRatingValue = selectedResort ? selectedResortRatingStat?.averageRating : ratingSummary?.overallAverage;
  const avgRatingCount = selectedResort ? selectedResortRatingStat?.reviewCount : ratingSummary?.totalReviews;

  return (
    <div className="admin-reviews-dashboard" style={{ padding: "0px 20px 20px 20px", boxSizing: "border-box" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {ConfirmDialogElement}

      <div className="vt-page-header">
        <h2>Reviews</h2>
      </div>

      <div className="review-tabs">
        <button
          type="button"
          className={`review-tab ${activeTab === "REVIEWS" ? "review-tab--active" : ""}`}
          onClick={() => setActiveTab("REVIEWS")}
        >
          Reviews
        </button>
        <button
          type="button"
          className={`review-tab ${activeTab === "OVERVIEW" ? "review-tab--active" : ""}`}
          onClick={() => setActiveTab("OVERVIEW")}
        >
          Overview
        </button>
        <button
          type="button"
          className={`review-tab ${activeTab === "WEBSITE_REVIEWS" ? "review-tab--active" : ""}`}
          onClick={() => setActiveTab("WEBSITE_REVIEWS")}
        >
          Website Reviews
        </button>
      </div>

      {activeTab === "REVIEWS" && (
        <>
          <div className="dashboard-filters">
            <div className="filter-item">
              <label>Resort</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={resorts}
                value={selectedResort}
                onChange={setSelectedResort}
                placeholder="All Resorts"
                isClearable
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>

            <div className="filter-item">
              <label>Status</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All statuses"
                isClearable
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>

            <div className="filter-item filter-item--rating">
              <label>Rating: {ratingMin === ratingMax ? `${ratingMin}★` : `${ratingMin}★ – ${ratingMax}★`}</label>
              <StarRangeSlider
                min={ratingMin}
                max={ratingMax}
                onChange={(newMin, newMax) => {
                  setRatingMin(newMin);
                  setRatingMax(newMax);
                }}
              />
            </div>

            <div className="filter-item">
              <label>From Date</label>
              <DatePicker
                selected={fromDate}
                onChange={setFromDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="Any"
                className="date-picker"
                portalId="reviews-datepicker-portal"
                isClearable
              />
            </div>

            <div className="filter-item">
              <label>To Date</label>
              <DatePicker
                selected={toDate}
                onChange={setToDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="Any"
                className="date-picker"
                portalId="reviews-datepicker-portal"
                isClearable
              />
            </div>

            <div className="filter-item">
              <label style={{ visibility: "hidden" }}>Reset</label>
              <button type="button" className="reset-filters-btn" onClick={resetFilters}>
                Reset
              </button>
            </div>

          </div>

          <div className="review-stat-row">
            <div className="review-stat-box">
              <div className="review-stat-label">GBP Review Count</div>
              <div className="review-stat-value">{totalElements}</div>
            </div>
            {isSuperAdmin && !ratingSummaryLoading && (
              <div className="review-stat-box">
                <div className="review-stat-label">Average Rating</div>
                <div className="review-stat-value">
                  {avgRatingValue != null ? `${avgRatingValue.toFixed(1)}★` : "-"}
                </div>
                {avgRatingCount != null && <div className="review-stat-sublabel">{avgRatingCount} reviews</div>}
              </div>
            )}
            <div className="review-stat-box review-stat-box--pending">
              <div className="review-stat-label">Pending Reply</div>
              <div className="review-stat-value">{pendingCount}</div>
            </div>
          </div>

          <h3 className="review-list-heading">Reviews List</h3>

          {loading ? (
            <p>Loading...</p>
          ) : reviews.length === 0 ? (
            <p>No reviews found</p>
          ) : (
            <div className="review-card-list">
              {reviews.map((r) => (
                <div
                  key={r.id}
                  className={`review-card ${r.starRating <= 3 ? "review-card--low" : ""} ${r.deletedAt ? "review-card--deleted" : ""}`}
                >
                  <div className="review-card-head">
                    <span className="review-card-name">{r.reviewerName || "Anonymous"}</span>
                    <div className="review-card-badges">
                      {r.editedAt && <span className="review-badge review-badge--blue">Edited</span>}
                      {r.deletedAt && <span className="review-badge review-badge--neutral">Deleted from Google</span>}
                      <StatusBadge status={r.replyStatus} />
                    </div>
                  </div>

                  <div className="review-card-meta">
                    <div className="review-card-resort">{r.resort?.name || "-"}</div>
                    {r.resort?.location && <div className="review-card-address">{r.resort.location}</div>}
                  </div>

                  <div className="review-card-line">
                    <span className="review-card-label">Posted On :</span>{" "}
                    {r.googleCreateTime ? formatDateDMY(r.googleCreateTime) : "-"}
                  </div>
                  <div className="review-card-line">
                    <span className="review-card-label">Rated :</span> <Stars n={r.starRating} />
                  </div>

                  {r.editedAt && (
                    <div className="review-card-edited-note">
                      Edited on {formatDateDMY(r.editedAt)}
                      {r.previousStarRating != null && ` - was rated ${r.previousStarRating}★`}
                      {r.previousReplyText && ` - our previous reply: "${r.previousReplyText}"`}
                    </div>
                  )}

                  <div className="review-card-comment">
                    <span className="review-card-label">Review comment:</span>
                    <p>{r.comment || <em>(no comment)</em>}</p>
                  </div>

                  {/* 4-5 star reviews are picked up and sent automatically by
                      the sync/auto-post pipeline - no manual draft step for
                      those. The manual fallback is for 1-3 star only, where
                      Gemini drafting can fail and staff need another way in. */}
                  {canManage && r.starRating <= 3 && (
                    <div className="review-card-actions">
                      <button
                        type="button"
                        className="vt-btn vt-btn-primary"
                        onClick={() => (expandedId === r.id ? closeDraft() : openDraft(r))}
                      >
                        {expandedId === r.id ? "Close" : "Reply"}
                      </button>
                    </div>
                  )}

                  {(r.replyStatus === "AUTO_SENT" || r.replyStatus === "SENT") && r.replyText && (
                    <div className="review-card-reply">
                      <span className="review-card-label">Our reply:</span>
                      <p>{r.replyText}</p>
                    </div>
                  )}

                  {expandedId === r.id && (
                    <div className="review-draft-panel">
                      <label>Reply - edit before sending:</label>
                      <textarea
                        rows={4}
                        value={draftText}
                        onChange={(e) => setDraftText(e.target.value)}
                        placeholder="Type a reply..."
                      />
                      <div className="review-draft-actions">
                        <button
                          type="button"
                          className="vt-btn vt-btn-primary"
                          disabled={busyIds.has(r.id)}
                          onClick={() => handleSend(r)}
                        >
                          Send
                        </button>
                        {r.starRating <= 3 && (
                          <button
                            type="button"
                            className="vt-btn vt-btn-purple"
                            disabled={busyIds.has(r.id)}
                            onClick={() => handleRegenerate(r)}
                          >
                            Regenerate (AI)
                          </button>
                        )}
                        {r.replyStatus !== "SENT" && r.replyStatus !== "AUTO_SENT" && (
                          <button
                            type="button"
                            className="vt-btn vt-btn-danger"
                            disabled={busyIds.has(r.id)}
                            onClick={() => handleDiscard(r)}
                          >
                            Discard
                          </button>
                        )}
                        <button type="button" className="vt-btn vt-btn-secondary" onClick={closeDraft}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="expenses-pagination">
              <button
                type="button"
                className="vt-btn vt-btn-secondary"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span className="expenses-pagination-info">Page {page + 1} of {totalPages}</span>
              <button
                type="button"
                className="vt-btn vt-btn-secondary"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === "OVERVIEW" && (
        <div className="review-overview">
          <div className="dashboard-filters">
            <div className="filter-item">
              <label>Resort</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={resorts}
                value={overviewResort}
                onChange={setOverviewResort}
                placeholder="All Resorts"
                isClearable
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>
            <div className="filter-item">
              <label style={{ visibility: "hidden" }}>Period</label>
              <div className="review-period-toggle">
                {OVERVIEW_PERIODS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    className={`review-period-btn ${overviewPeriod === p.value ? "review-period-btn--active" : ""}`}
                    onClick={() => setOverviewPeriod(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="review-overview-content">
          {overviewLoading ? (
            <p>Loading...</p>
          ) : !overviewData ? (
            <p>No data</p>
          ) : (
            <>
              <div className="review-stat-row">
                <div className="review-stat-box">
                  <div className="review-stat-label">Reviews Received</div>
                  <div className="review-stat-value">{overviewData.totalReviews}</div>
                  <ReviewCountTrend
                    current={overviewData.totalReviews}
                    previous={overviewData.previous?.totalReviews}
                    period={overviewPeriod}
                  />
                </div>
                <div className="review-stat-box">
                  <div className="review-stat-label">Average Rating</div>
                  <div className="review-stat-value">
                    {overviewData.averageRating != null ? `${overviewData.averageRating.toFixed(1)}★` : "-"}
                  </div>
                  <AverageRatingTrend
                    current={overviewData.averageRating}
                    previous={overviewData.previous?.averageRating}
                    period={overviewPeriod}
                  />
                </div>
                {isSuperAdmin && !ratingSummaryLoading && (
                  <div className="review-stat-box">
                    <div className="review-stat-label">Vintara Avg Rating</div>
                    <div className="review-stat-value">
                      {ratingSummary?.overallAverage != null ? `${ratingSummary.overallAverage.toFixed(1)}★` : "-"}
                    </div>
                    <div className="review-stat-sublabel">All-time, across every resort</div>
                  </div>
                )}
              </div>

              <h3 className="review-list-heading">Rating Breakdown</h3>
              <div className="review-rating-bars">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = overviewData.ratingBreakdown?.[star] ?? 0;
                  const pct = overviewData.totalReviews > 0 ? (count / overviewData.totalReviews) * 100 : 0;
                  return (
                    <div key={star} className="review-rating-bar-row">
                      <span className="review-rating-bar-label">{star}★</span>
                      <div className="review-rating-bar">
                        <div className="review-rating-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="review-rating-bar-count">{count}</span>
                    </div>
                  );
                })}
              </div>

              <h3 className="review-list-heading">Reply Status</h3>
              <div className="review-status-breakdown-row">
                {Object.entries(overviewData.statusBreakdown || {}).map(([status, count]) => (
                  <div key={status} className="review-status-breakdown-item">
                    <StatusBadge status={status} />
                    <span className="review-status-breakdown-count">{count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          </div>
        </div>
      )}

      {activeTab === "WEBSITE_REVIEWS" && (
        <>
          <p className="review-template-help" style={{ marginBottom: 16 }}>
            Guest-submitted reviews from Vintara&apos;s own site (not Google) - approve to make one visible on
            that resort&apos;s microsite, or reject to keep it hidden.
          </p>

          <div className="dashboard-filters">
            <div className="filter-item">
              <label>Resort</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={resorts}
                value={guestReviewResort}
                onChange={setGuestReviewResort}
                placeholder="All Resorts"
                isClearable
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>

            <div className="filter-item">
              <label>Status</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={GUEST_REVIEW_STATUS_OPTIONS}
                value={guestReviewStatus}
                onChange={setGuestReviewStatus}
                placeholder="All statuses"
                isClearable
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>
          </div>

          {guestReviewsLoading ? (
            <p>Loading...</p>
          ) : guestReviews.length === 0 ? (
            <p>No reviews found</p>
          ) : (
            <div className="review-card-list">
              {guestReviews.map((r) => (
                <div key={r.id} className="review-card">
                  <div className="review-card-head">
                    <span className="review-card-name">{r.reviewerName || "Guest"}</span>
                    <GuestReviewStatusBadge status={r.status} />
                  </div>

                  <div className="review-card-meta">
                    <div className="review-card-resort">{r.resort?.name || "-"}</div>
                    {r.resort?.location && <div className="review-card-address">{r.resort.location}</div>}
                  </div>

                  <div className="review-card-line">
                    <span className="review-card-label">Submitted On :</span>{" "}
                    {r.submittedAt ? formatDateDMY(r.submittedAt) : "-"}
                  </div>
                  <div className="review-card-line">
                    <span className="review-card-label">Rated :</span> <Stars n={r.rating} />
                  </div>

                  <div className="review-card-comment">
                    <span className="review-card-label">Review comment:</span>
                    <p>{r.comment || <em>(no comment)</em>}</p>
                  </div>

                  {canManage && r.status === "PENDING" && (
                    <div className="review-card-actions">
                      <button
                        type="button"
                        className="vt-btn vt-btn-primary"
                        disabled={guestReviewBusyIds.has(r.id)}
                        onClick={() => handleApproveGuestReview(r)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="vt-btn vt-btn-purple"
                        disabled={guestReviewBusyIds.has(r.id)}
                        onClick={() => handleRejectGuestReview(r)}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {guestReviewsTotalPages > 1 && (
            <div className="expenses-pagination">
              <button
                type="button"
                className="vt-btn vt-btn-secondary"
                onClick={() => setGuestReviewsPage((p) => Math.max(0, p - 1))}
                disabled={guestReviewsPage === 0}
              >
                Previous
              </button>
              <span className="expenses-pagination-info">
                Page {guestReviewsPage + 1} of {guestReviewsTotalPages}
              </span>
              <button
                type="button"
                className="vt-btn vt-btn-secondary"
                onClick={() => setGuestReviewsPage((p) => Math.min(guestReviewsTotalPages - 1, p + 1))}
                disabled={guestReviewsPage >= guestReviewsTotalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default AdminReviewsDashboard;
